// Offline outbox for Trial/Meeting creates (Phase 3).
// CSRF token / cookie / HTML / files は保存しない。

import {
  OUTBOX_STORE,
  idbDelete,
  idbGet,
  idbGetAll,
  idbSet
} from "./idb"
import { getIdentity } from "./snapshot"

export const OUTBOX_OPERATION = "schedule.create"
export const ALLOWED_SCHEDULE_TYPES = new Set(["Trial", "Meeting"])
export const ALLOWED_PAYLOAD_KEYS = [
  "matter_id",
  "user_id",
  "start",
  "end",
  "courtroom",
  "note",
  "sche_type",
  "name",
  "order",
  "type",
  "duration",
  "assignee_id",
  "contact_id",
  "use_mail",
  "use_calendar",
  "web_meeting"
]

export const OUTBOX_STATUS_EVENT = "firmee:pwa-outbox"

const INITIAL_BACKOFF_MS = 5000
const MAX_BACKOFF_MS = 5 * 60 * 1000

let flushing = false

export function canUseOutbox(identity) {
  return Boolean(identity?.enabled && identity?.partition)
}

export function entryMatchesIdentity(entry, identity) {
  return canUseOutbox(identity) && entry.partition === identity.partition
}

export function publishOutboxStatus(detail) {
  if (typeof window === "undefined") return
  window.dispatchEvent(new CustomEvent(OUTBOX_STATUS_EVENT, { detail }))
}

export function csrfTokenFromDocument(doc = document) {
  const meta = doc.querySelector?.("meta[name='csrf-token']")
  if (meta?.content) return meta.content

  const form = doc.querySelector?.('form[data-controller~="offline-submit"]')
  const input =
    form?.querySelector?.('input[name="authenticity_token"]') ||
    doc.querySelector?.('input[name="authenticity_token"]')
  return input?.value || null
}

export function filterAllowedPayload(raw = {}) {
  const payload = {}
  for (const key of ALLOWED_PAYLOAD_KEYS) {
    if (Object.prototype.hasOwnProperty.call(raw, key) && raw[key] != null) {
      payload[key] = String(raw[key])
    }
  }
  return payload
}

export async function digestPayload(payload) {
  const canonical = Object.keys(payload)
    .sort()
    .reduce((acc, key) => {
      acc[key] = payload[key] == null ? null : String(payload[key])
      return acc
    }, {})
  const encoded = new TextEncoder().encode(JSON.stringify(canonical))
  const digest = await crypto.subtle.digest("SHA-256", encoded)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("")
}

export function formDataToPayload(formData) {
  const raw = {}
  for (const key of ALLOWED_PAYLOAD_KEYS) {
    const namespaced = `schedule[${key}]`
    if (formData.has(namespaced)) {
      // Rails の scalar parameter と同様、hidden の後にある選択値を採用する。
      raw[key] = formData.getAll(namespaced).at(-1)
    } else if (formData.has(key)) {
      raw[key] = formData.getAll(key).at(-1)
    }
  }
  return filterAllowedPayload(raw)
}

export function formHasAttachments(formData) {
  for (const [key, value] of formData.entries()) {
    if (!/\[files\]|files/i.test(key)) continue
    if (typeof File !== "undefined" && value instanceof File && value.size > 0) return true
    if (value && typeof value === "object" && "size" in value && value.size > 0) return true
  }
  return false
}

export async function enqueueScheduleCreate({
  requestId,
  payload,
  partition = null,
  identity = null,
  now = Date.now()
}) {
  if (!requestId) throw new Error("request_id required")
  const resolvedIdentity = identity || (await getIdentity().catch(() => ({ enabled: false, partition: null })))
  if (!canUseOutbox(resolvedIdentity)) {
    throw new Error("offline storage is not enabled")
  }
  if (partition != null && partition !== resolvedIdentity.partition) {
    throw new Error("partition mismatch")
  }

  const filtered = filterAllowedPayload(payload)
  if (!ALLOWED_SCHEDULE_TYPES.has(filtered.type)) {
    throw new Error("unsupported schedule type")
  }
  const entry = {
    request_id: requestId,
    partition: resolvedIdentity.partition,
    operation: OUTBOX_OPERATION,
    schedule_type: filtered.type,
    payload: filtered,
    payload_digest: await digestPayload(filtered),
    created_at: new Date(now).toISOString(),
    state: "queued",
    attempts: 0,
    last_error_code: null,
    next_attempt_at: new Date(now).toISOString()
  }
  await idbSet(OUTBOX_STORE, requestId, entry)
  await publishQueueCount()
  return entry
}

export async function listOutboxEntries() {
  const rows = await idbGetAll(OUTBOX_STORE)
  return rows.sort((a, b) => {
    const byCreated = String(a.created_at).localeCompare(String(b.created_at))
    if (byCreated !== 0) return byCreated
    return String(a.request_id).localeCompare(String(b.request_id))
  })
}

export async function listOutboxEntriesForCurrentPartition() {
  const identity = await getIdentity().catch(() => ({ enabled: false, partition: null }))
  if (!canUseOutbox(identity)) return []
  return (await listOutboxEntries()).filter((entry) => entryMatchesIdentity(entry, identity))
}

export async function countOutboxEntries() {
  return (await listOutboxEntriesForCurrentPartition()).length
}

export async function discardOutboxEntry(requestId) {
  const identity = await getIdentity().catch(() => ({ enabled: false, partition: null }))
  if (!canUseOutbox(identity)) {
    throw new Error("offline storage is not enabled")
  }

  const entry = await idbGet(OUTBOX_STORE, requestId)
  if (!entry) return
  if (!entryMatchesIdentity(entry, identity)) {
    throw new Error("partition mismatch")
  }

  await idbDelete(OUTBOX_STORE, requestId)
  await publishQueueCount()
}

export async function publishQueueCount() {
  const identity = await getIdentity().catch(() => ({ enabled: false, partition: null }))
  if (!canUseOutbox(identity)) {
    publishOutboxStatus({ state: "outbox-empty", count: 0 })
    return 0
  }

  const entries = await listOutboxEntriesForCurrentPartition()
  const count = entries.length
  const hasAuthEntry = entries.some((entry) => entry.state === "queued" && entry.last_error_code === "auth")
  if (hasAuthEntry) {
    publishOutboxStatus({ state: "auth-expired", count })
    return count
  }

  publishOutboxStatus({ state: count > 0 ? "outbox" : "outbox-empty", count })
  return count
}

export function classifyFlushResponse(status, { networkError = false } = {}) {
  if (networkError) return { action: "retry", errorCode: "network" }
  if (status === 401 || status === 419) return { action: "auth", errorCode: "auth" }
  if (status === 403 || status === 404 || status === 422 || status === 409) {
    return { action: "fail", errorCode: String(status) }
  }
  if (!status || status >= 500) return { action: "retry", errorCode: "server" }
  if (status === 200 || status === 201) return { action: "success", errorCode: null }
  return { action: "retry", errorCode: "server" }
}

export function nextBackoffMs(attempts) {
  const exp = Math.min(MAX_BACKOFF_MS, INITIAL_BACKOFF_MS * 2 ** Math.max(0, attempts - 1))
  return exp
}

export async function hasRetryableQueuedEntries({ identity, now = Date.now() } = {}) {
  const resolvedIdentity = identity || (await getIdentity().catch(() => ({ enabled: false, partition: null })))
  if (!canUseOutbox(resolvedIdentity)) return false

  return (await listOutboxEntries()).some((entry) => {
    if (!entryMatchesIdentity(entry, resolvedIdentity)) return false
    if (entry.state !== "queued") return false
    if (entry.last_error_code === "auth") return false
    if (entry.next_attempt_at && Date.parse(entry.next_attempt_at) > now) return false
    return true
  })
}

// Background Sync は Chromium 系の補助経路。セッション/CSRF 失効時は成功保証できない。
export async function registerOutboxBackgroundSync(
  serviceWorker = typeof navigator !== "undefined" ? navigator.serviceWorker : null,
  { identity, now } = {}
) {
  if (!(await hasRetryableQueuedEntries({ identity, now }))) return false

  try {
    const reg = await serviceWorker?.ready
    if (reg?.sync?.register) {
      await reg.sync.register("firmee-outbox-flush")
      return true
    }
  } catch (_error) {
    // Background Sync 非対応・拒否は無視（foreground / online が主経路）
  }
  return false
}

async function markEntry(entry, patch) {
  const next = { ...entry, ...patch }
  await idbSet(OUTBOX_STORE, entry.request_id, next)
  return next
}

export async function flushOutbox({
  fetchImpl = fetch,
  csrfToken,
  now = Date.now(),
  registerBackgroundSync = registerOutboxBackgroundSync
} = {}) {
  if (flushing) return { flushed: 0, failed: 0, deferred: 0 }
  flushing = true
  let flushed = 0
  let failed = 0
  let deferred = 0

  try {
    const identity = await getIdentity().catch(() => ({ enabled: false, partition: null }))
    if (!canUseOutbox(identity)) {
      return { flushed: 0, failed: 0, deferred: 0 }
    }

    const entries = await listOutboxEntries()

    for (const entry of entries) {
      if (!entryMatchesIdentity(entry, identity)) {
        continue
      }
      if (entry.state === "failed") {
        failed += 1
        continue
      }
      if (entry.next_attempt_at && Date.parse(entry.next_attempt_at) > now) {
        deferred += 1
        continue
      }

      const token = csrfToken ?? csrfTokenFromDocument()
      if (!token) {
        await markEntry(entry, {
          state: "queued",
          last_error_code: "auth",
          next_attempt_at: new Date(now).toISOString()
        })
        publishOutboxStatus({ state: "auth-expired", count: await countOutboxEntries() })
        deferred += 1
        break
      }

      await markEntry(entry, { state: "sending" })

      let response
      try {
        response = await fetchImpl("/offline/schedules", {
          method: "POST",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": token
          },
          body: JSON.stringify({
            client_request_id: entry.request_id,
            schedule: entry.payload
          })
        })
      } catch (_error) {
        const attempts = (entry.attempts || 0) + 1
        await markEntry(entry, {
          state: "queued",
          attempts,
          last_error_code: "network",
          next_attempt_at: new Date(now + nextBackoffMs(attempts)).toISOString()
        })
        deferred += 1
        continue
      }

      const classification = classifyFlushResponse(response.status)
      if (classification.action === "success") {
        await idbDelete(OUTBOX_STORE, entry.request_id)
        flushed += 1
        continue
      }

      if (classification.action === "auth") {
        await markEntry(entry, {
          state: "queued",
          last_error_code: "auth",
          next_attempt_at: new Date(now).toISOString()
        })
        publishOutboxStatus({ state: "auth-expired", count: await countOutboxEntries() })
        deferred += 1
        break
      }

      if (classification.action === "fail") {
        await markEntry(entry, {
          state: "failed",
          attempts: (entry.attempts || 0) + 1,
          last_error_code: classification.errorCode
        })
        failed += 1
        continue
      }

      const attempts = (entry.attempts || 0) + 1
      await markEntry(entry, {
        state: "queued",
        attempts,
        last_error_code: classification.errorCode,
        next_attempt_at: new Date(now + nextBackoffMs(attempts)).toISOString()
      })
      deferred += 1
    }

    await registerBackgroundSync(undefined, { identity, now })
    return { flushed, failed, deferred }
  } finally {
    flushing = false
    await publishQueueCount().catch(() => {})
  }
}

export function startOutboxFlushScheduler() {
  let timer = null
  let stopped = false
  let planning = 0

  const scheduleRetry = async () => {
    const version = ++planning
    clearTimeout(timer)
    timer = null
    if (stopped || flushing || navigator.onLine === false) return
    const entries = await listOutboxEntriesForCurrentPartition()
    if (stopped || flushing || version !== planning) return
    const deadlines = entries
      .filter((entry) => entry.state === "queued" && entry.last_error_code !== "auth")
      .map((entry) => Date.parse(entry.next_attempt_at || entry.created_at))
      .filter(Number.isFinite)
    if (deadlines.length === 0) return
    timer = setTimeout(run, Math.max(0, Math.min(...deadlines) - Date.now()))
  }

  const run = async () => {
    clearTimeout(timer)
    timer = null
    if (stopped || navigator.onLine === false) return
    try {
      await flushOutbox()
    } catch (_error) {
      // 次の online / turbo:load でも再試行する。
    } finally {
      scheduleRetry().catch(() => {})
    }
  }
  const onQueueChange = () => { scheduleRetry().catch(() => {}) }
  const onMessage = (event) => {
    if (event?.data?.type === "pwa-outbox-flush") run()
  }
  const serviceWorker = navigator.serviceWorker
  window.addEventListener("online", run)
  window.addEventListener("offline", onQueueChange)
  window.addEventListener(OUTBOX_STATUS_EVENT, onQueueChange)
  document.addEventListener("turbo:load", run)
  serviceWorker?.addEventListener("message", onMessage)
  run()

  return () => {
    stopped = true
    planning += 1
    clearTimeout(timer)
    window.removeEventListener("online", run)
    window.removeEventListener("offline", onQueueChange)
    window.removeEventListener(OUTBOX_STATUS_EVENT, onQueueChange)
    document.removeEventListener("turbo:load", run)
    serviceWorker?.removeEventListener("message", onMessage)
  }
}

export async function getOutboxEntry(requestId) {
  return idbGet(OUTBOX_STORE, requestId)
}
